<template>
  <aside class="estrut-sidebar">
    <div class="estrut-sidebar__portrait-wrap">
      <img
        :src="portraitSrc"
        :alt="t('estruturacao.heroAlt')"
        class="estrut-sidebar__portrait"
      />
    </div>

    <div v-if="isSuperAdmin" class="estrut-sidebar__edit-bar">
      <button
        type="button"
        class="estrut-sidebar__edit-toggle"
        :class="{ 'estrut-sidebar__edit-toggle--active': editMode }"
        @click="$emit('toggle-edit')"
      >
        {{ editMode ? t('estruturacao.lessons.editModeOff') : t('estruturacao.lessons.editModeOn') }}
      </button>
    </div>

    <EstruturacaoAdminEditor
      v-if="editMode && isSuperAdmin"
      ref="adminEditorRef"
      :modulo="section.screenId"
      :tree="tree"
      @refresh="$emit('refresh')"
    />

    <nav class="estrut-sidebar__nav" :aria-label="t('estruturacao.lessons.navigation')">
      <div v-if="!tree?.categorias?.length" class="estrut-sidebar__empty">
        {{ t('estruturacao.lessons.noCategories') }}
      </div>

      <div
        v-for="cat in tree?.categorias || []"
        :key="cat.id"
        class="estrut-sidebar__category"
      >
        <div class="estrut-sidebar__cat-row">
          <button
            type="button"
            class="estrut-sidebar__cat-toggle"
            :class="{ 'estrut-sidebar__cat-toggle--inactive': isSuperAdmin && cat.ativo === false }"
            @click="toggleCategory(cat.id)"
          >
            <span class="estrut-sidebar__cat-toggle-label">{{ cat.titulo }}</span>
            <span
              v-if="isSuperAdmin && cat.ativo === false"
              class="estrut-sidebar__hidden-icon"
              :title="t('estruturacao.lessons.hiddenFromStudents')"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            </span>
            <svg
              class="estrut-sidebar__chevron"
              :class="{ 'estrut-sidebar__chevron--open': openCategories.has(cat.id) }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div v-if="editMode && isSuperAdmin" class="estrut-sidebar__cat-actions">
            <button
              type="button"
              class="estrut-sidebar__action-btn"
              :title="t('common.edit')"
              @click="adminEditorRef?.openEditCategory(cat)"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              type="button"
              class="estrut-sidebar__action-btn estrut-sidebar__action-btn--danger"
              :title="t('common.delete')"
              @click="adminEditorRef?.openDeleteCategory(cat)"
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        <ul v-show="openCategories.has(cat.id)" class="estrut-sidebar__aulas">
          <li v-for="(aula, aulaIdx) in cat.aulas || []" :key="aula.id">
            <div class="estrut-sidebar__aula-row">
              <button
                type="button"
                class="estrut-sidebar__aula"
                :class="{
                  'estrut-sidebar__aula--active': selectedAulaId === aula.id,
                  'estrut-sidebar__aula--locked': !isAulaUnlocked(aula),
                  'estrut-sidebar__aula--inactive': isSuperAdmin && aula.ativo === false
                }"
                @click="$emit('select-aula', aula.id)"
              >
                <span class="estrut-sidebar__aula-num">{{ formatLessonNum(aulaIdx) }}</span>
                <span class="estrut-sidebar__aula-title">{{ aula.titulo }}</span>
                <span
                  v-if="isSuperAdmin && isHiddenFromStudents(aula, cat)"
                  class="estrut-sidebar__hidden-icon estrut-sidebar__hidden-icon--sm"
                  :title="t('estruturacao.lessons.hiddenFromStudents')"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                </span>
                <span v-if="aula.concluida" class="estrut-sidebar__aula-done" :title="t('estruturacao.lessons.completed')">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span v-else-if="!isAulaUnlocked(aula)" class="estrut-sidebar__aula-lock" :title="t('estruturacao.lessons.lessonLocked')">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </span>
              </button>
              <div v-if="editMode && isSuperAdmin" class="estrut-sidebar__aula-actions">
                <button
                  type="button"
                  class="estrut-sidebar__action-btn"
                  :title="t('common.edit')"
                  @click.stop="adminEditorRef?.openEditAula(aula, cat.id)"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="estrut-sidebar__action-btn estrut-sidebar__action-btn--danger"
                  :title="t('common.delete')"
                  @click.stop="adminEditorRef?.openDeleteAula(aula)"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </li>
          <li v-if="editMode && isSuperAdmin" class="estrut-sidebar__add-lesson">
            <button
              type="button"
              class="estrut-sidebar__add-lesson-btn"
              @click="adminEditorRef?.openCreateLesson(cat.id)"
            >
              + {{ t('estruturacao.lessons.newLesson') }}
            </button>
          </li>
        </ul>
      </div>
    </nav>
  </aside>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useThemeStore } from '../../stores/theme'
import heroDark from '../../assets/estruturacao/édipo Oliveira.png'
import heroLight from '../../assets/estruturacao/édipo Oliveira 2.png'
import EstruturacaoAdminEditor from './EstruturacaoAdminEditor.vue'

const props = defineProps({
  section: { type: Object, required: true },
  tree: { type: Object, default: null },
  selectedAulaId: { type: Number, default: null },
  editMode: { type: Boolean, default: false },
  isSuperAdmin: { type: Boolean, default: false }
})

defineEmits(['select-aula', 'toggle-edit', 'refresh'])

const { t } = useI18n()
const themeStore = useThemeStore()

const adminEditorRef = ref(null)
const openCategories = ref(new Set())

const portraitSrc = computed(() => (themeStore.isLight ? heroLight : heroDark))

function formatLessonNum(index) {
  return String(index + 1).padStart(2, '0')
}

function isAulaUnlocked(aula) {
  return props.isSuperAdmin || aula.liberada
}

function isHiddenFromStudents(aula, cat) {
  return aula.ativo === false || cat.ativo === false
}

function toggleCategory(id) {
  const next = new Set(openCategories.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  openCategories.value = next
}

watch(
  () => props.tree,
  (tree) => {
    if (!tree?.categorias?.length) return
    openCategories.value = new Set(tree.categorias.map((c) => c.id))
  },
  { immediate: true }
)
</script>

<style scoped>
.estrut-sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
}

.estrut-sidebar__portrait-wrap {
  display: flex;
  justify-content: center;
}

.estrut-sidebar__portrait {
  width: 100%;
  max-width: 220px;
  height: auto;
  object-fit: contain;
  border-radius: 4px;
}

@media (min-width: 1024px) {
  .estrut-sidebar__portrait {
    max-width: 100%;
  }
}

.estrut-sidebar__edit-bar {
  display: flex;
  justify-content: flex-end;
}

.estrut-sidebar__edit-toggle {
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
}

.estrut-sidebar__edit-toggle--active {
  border-color: var(--accent-primary);
  color: var(--accent-primary);
  background: color-mix(in srgb, var(--accent-primary) 10%, transparent);
}

.estrut-sidebar__nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  max-height: 50vh;
}

@media (min-width: 1024px) {
  .estrut-sidebar__nav {
    max-height: calc(100vh - 320px);
  }
}

.estrut-sidebar__empty {
  padding: 16px;
  text-align: center;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.estrut-sidebar__category {
  margin-bottom: 8px;
}

.estrut-sidebar__cat-row {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.estrut-sidebar__cat-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.estrut-sidebar__cat-toggle-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.estrut-sidebar__hidden-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #d97706;
}

.estrut-sidebar__hidden-icon svg {
  width: 16px;
  height: 16px;
}

.estrut-sidebar__hidden-icon--sm svg {
  width: 14px;
  height: 14px;
}

body.tema_escura .estrut-sidebar__hidden-icon {
  color: #fbbf24;
}

.estrut-sidebar__cat-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.estrut-sidebar__chevron {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform 0.2s;
}

.estrut-sidebar__chevron--open {
  transform: rotate(180deg);
}

.estrut-sidebar__aulas {
  list-style: none;
  margin: 4px 0 0;
  padding: 0 0 0 8px;
}

.estrut-sidebar__aula-row {
  display: flex;
  align-items: stretch;
  gap: 4px;
  margin-top: 2px;
}

.estrut-sidebar__aula {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.estrut-sidebar__aula:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.estrut-sidebar__aula--active {
  background: color-mix(in srgb, var(--accent-primary) 12%, transparent);
  color: var(--accent-primary);
  font-weight: 600;
}

.estrut-sidebar__aula--locked {
  opacity: 0.65;
}

.estrut-sidebar__aula--inactive {
  border: 1px dashed color-mix(in srgb, var(--text-tertiary) 45%, transparent);
  opacity: 0.85;
}

.estrut-sidebar__cat-toggle--inactive {
  opacity: 0.75;
  font-style: italic;
}

.estrut-sidebar__aula-num {
  flex-shrink: 0;
  width: 24px;
  font-variant-numeric: tabular-nums;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--text-tertiary);
}

.estrut-sidebar__aula--active .estrut-sidebar__aula-num {
  color: var(--accent-primary);
}

.estrut-sidebar__aula-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.estrut-sidebar__aula-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
  align-items: center;
}

.estrut-sidebar__action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: var(--bg-secondary);
  color: var(--text-tertiary);
  cursor: pointer;
}

.estrut-sidebar__action-btn svg {
  width: 14px;
  height: 14px;
}

.estrut-sidebar__action-btn:hover {
  color: var(--text-primary);
  background: var(--bg-primary);
}

.estrut-sidebar__action-btn--danger:hover {
  color: #dc2626;
  background: color-mix(in srgb, #dc2626 10%, transparent);
}

.estrut-sidebar__aula-done svg,
.estrut-sidebar__aula-lock svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.estrut-sidebar__aula-done {
  color: #059669;
}

.estrut-sidebar__aula-lock {
  color: var(--text-tertiary);
}

.estrut-sidebar__add-lesson {
  margin-top: 6px;
  padding-left: 8px;
}

.estrut-sidebar__add-lesson-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 10px;
  border: 1px dashed var(--accent-primary);
  border-radius: 4px;
  background: color-mix(in srgb, var(--accent-primary) 6%, transparent);
  color: var(--accent-primary);
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.estrut-sidebar__add-lesson-btn:hover {
  background: color-mix(in srgb, var(--accent-primary) 12%, transparent);
}
</style>
